import { Alert, Button, Form, Input, Table } from 'antd';
import { useState } from 'react';
import { useCanManageService } from '../../../hooks/use-can-manage-service';
import { useDirectoryTags } from '../../../hooks/use-directory-tags';
import type { DirectoryTagKind, DirectoryTagView } from '../directory-tags.api';
import { useDirectoryTagActions } from '../hooks/use-directory-tag-actions';
import { buildDirectoryTagColumns } from './build-directory-tag-columns';
import { RenameDirectoryTagModal } from './RenameDirectoryTagModal';

interface DirectoryTagsPanelProps {
  kind: DirectoryTagKind;
  title: string;
  description: string;
}

/** One catalogue (garage service types or supplier categories): add an entry, rename it, deactivate or reactivate it. Never deleted. */
export function DirectoryTagsPanel({ kind, title, description }: DirectoryTagsPanelProps) {
  const { tags, refresh } = useDirectoryTags(kind);
  const actions = useDirectoryTagActions(kind, refresh);
  const canManage = useCanManageService();
  const [addForm] = Form.useForm<{ name: string }>();
  const [renaming, setRenaming] = useState<DirectoryTagView | null>(null);

  async function add({ name }: { name: string }) {
    if (await actions.add(name)) addForm.resetFields();
  }

  return (
    <section aria-label={title} className="flex flex-col gap-3 rounded-lg border border-line bg-surface p-4">
      <div>
        <h2 className="m-0 text-base font-medium">{title}</h2>
        <p className="m-0 text-sm text-muted">{description}</p>
      </div>
      {actions.error && !renaming && <Alert type="error" title={actions.error} showIcon />}
      {canManage && (
        <Form form={addForm} layout="inline" onFinish={add}>
          <Form.Item name="name" rules={[{ required: true, whitespace: true, message: 'Enter a name' }]}>
            <Input placeholder="New entry" aria-label={`New ${title.toLowerCase()} entry`} />
          </Form.Item>
          <Button htmlType="submit" loading={actions.saving && !renaming}>
            Add
          </Button>
        </Form>
      )}
      <Table<DirectoryTagView>
        size="small"
        rowKey="id"
        dataSource={tags}
        pagination={false}
        columns={buildDirectoryTagColumns(canManage ? { onEdit: setRenaming, onDeactivate: actions.deactivate, onReactivate: actions.reactivate } : undefined)}
      />
      <RenameDirectoryTagModal
        tag={renaming}
        error={actions.error}
        saving={actions.saving}
        onSubmit={actions.rename}
        onClose={() => {
          setRenaming(null);
          actions.clearError();
        }}
      />
    </section>
  );
}
